import React, { useEffect, useState, useMemo } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Routes, Route } from "react-router-dom";
import RequestForQuote from "./rfq/screen/RequestForQuote";
import { getDataFromLocalStorage, getQueryParam, setDataInLocalStorage } from "./common/util";
import ConfigContext from "./common/context/ConfigContext";
import Verify from "./rfq/screen/Verify";
import Order from "./order/screen/Order";
import Header from "./common/component/Header";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import bkstApi from "./common/api";
import Footer from "./common/component/Footer";
import { STORAGE_BUCKET } from "./common/constants";
import OrderReview from "./order/screen/OrderReview";
import OrderBalancePay from "./order/screen/OrderBalancePay";
import PaymentStatusPage from "./order/screen/PaymentStatusPage";
import FaqPage from "./common/screen/FaqPage";
import Home from "./home/screen/Home";
import ProductPage from "./product/screen/ProductPage";
import AppStateContext from "./common/context/AppStateContext";
import ClassPage from "./classes/screen/ClassPage";
import RentalPage from "./rental/screen/RentalPage";
import PhotoCollection from "./home/screen/PhotoCollection";
import Unsubscribe from "./quote/screen/Unsubscribe";
import Cart from "./quote/screen/Cart";
import CartV2 from "./quote/screen/CartV2";
import Booking from "./quote/screen/Booking";
import Classes from "./classes/screen/Classes";
import WebFont from "webfontloader";
import FlavorsPage from "./home/screen/FlavorsPage";
import StorePolicyPage from "./home/screen/StorePolicyPage";
import ScrollToTop from "./common/component/ScrollToTop";
import CanonicalLink from "./common/component/CanonicalLink";
import CustomPage from "./home/screen/CustomPage";
import PaymentReceipt from "./order/screen/PaymentReceipt";
import Quote from "./quote/screen/Quote";
import StandardCatalog from "./product/screen/StandardCatalog";
import AccessibilityStatement from "./home/screen/AccessibilityStatement";
import SiteBanner from "./common/component/SiteBanner";

export default function App(props) {
  const [siteConfig, setSiteConfig] = useState();

  const getConfig = () => {
    bkstApi(`/get-config`).then((res) => {
      processConfig(res.data);
    });
  };

  const processConfig = (config) => {
    setSiteConfig(config);

    config?.theme?.typography?.fontFamily &&
      WebFont.load({
        google: {
          families: config?.theme?.typography?.fontFamily,
        },
      });

    document.title = config.biz;

    const favicon = document.getElementById("favicon");
    favicon.href = `${STORAGE_BUCKET}/img/b/${config.id}/logo400.webp`;
  };

  useEffect(() => {
    getConfig();

    // utm_campaign always overrides last-click attribution
    const utmc = getQueryParam(window.location.search, "utm_campaign");
    if (utmc) {
      const utms = getQueryParam(window.location.search, "utm_source");
      const utmm = getQueryParam(window.location.search, "utm_medium");

      setDataInLocalStorage("utm", { utms, utmm, utmc });
      console.log("utm", { utms, utmm, utmc });

      // TODO: Remove after St Patricks Day
      let hash = window.location.hash;
      if (hash === "#st.-patrick's-day2") {
        hash = "#st.-patrick's-day";
      }

      window.history.replaceState(null, "", `${window.location.pathname}${hash}`);
    } else {
      var referrer = document.referrer;
      if (referrer) {
        var a = document.createElement("a");
        a.href = referrer;

        const utm = getDataFromLocalStorage("utm", 7 * 24 * 60 * 60 * 1000);
        if (!utm || !utm.utmc) {
          setDataInLocalStorage("utm", { utms: a.hostname, utmm: "", utmc: "" });
        }
      }
    }
  }, []);

  const theme = useMemo(() => {
    const themeColor = siteConfig?.theme.palette.primary || "#DDD";
    const fontFamily =
      siteConfig?.theme?.typography?.fontFamily?.[0] ||
      `"Roboto", "Helvetica", "Arial", sans-serif`;
    const headingFontFamily = siteConfig?.theme?.typography?.h1?.fontFamily || fontFamily;
    const primaryButtonTextColor =
      siteConfig?.theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.color || "#FFF";

    return createTheme({
      palette: {
        primary: { main: themeColor },
        secondary: {
          main: "#818181",
        },
        background: {
          default: "#F7F7F7",
        },
        error: {
          main: "#d63031",
        },
        text: {
          secondary: "#6B6B6B",
        },
      },
      typography: {
        fontFamily,
        h1: {
          fontSize: "2rem",
          fontWeight: "700",
          fontFamily: headingFontFamily,
        },
        h2: {
          fontSize: "1.6rem",
          fontWeight: "600",
          fontFamily: headingFontFamily,
        },
        h3: {
          fontSize: "1.3rem",
          fontWeight: "600",
          fontFamily: headingFontFamily,
        },
        h4: {
          fontSize: "1.15rem",
          fontWeight: "700",
          fontFamily: headingFontFamily,
        },
        h5: {
          fontSize: "1.05rem",
          fontWeight: "700",
          fontFamily: headingFontFamily,
        },
        h6: {
          fontSize: "1rem",
          fontWeight: "700",
          fontFamily: headingFontFamily,
        },
        subtitle1: {
          fontSize: "1.1rem",
        },
        overline: {
          lineHeight: "2",
        },
      },
      components: {
        MuiAlert: {
          styleOverrides: {
            standardInfo: {
              color: "#01579b",
            },
            filledSuccess: {
              color: "#fff",
              backgroundColor: "#10ac84",
            },
            filledWarning: {
              backgroundColor: "#ff9f43",
            },
          },
        },
        MuiAlertTitle: {
          styleOverrides: {
            root: {
              fontWeight: "600",
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            popper: {
              zIndex: 70,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: ".75rem",
            },
          },
        },
        MuiTabScrollButton: {
          styleOverrides: {
            root: {
              "&.Mui-disabled": {
                opacity: 0.3,
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              color: primaryButtonTextColor,
            },
            containedSecondary: {
              backgroundColor: "#6B6B6B",
            },
          },
        },
        MuiTypography: {
          defaultProps: {
            variantMapping: {
              subtitle1: "p", // Render subtitle1 as <p> instead of <h6>
              subtitle2: "p",
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                backgroundColor: themeColor,
                color: "#FFFFFF",
              },
              "&.Mui-selected:hover": {
                backgroundColor: themeColor,
              },
              "&.MuiToggleButton-primary": {
                border: `1px solid ${themeColor}`,
              },
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              backgroundColor: "#f7f7f7",
              borderBottom: "1px solid #eee",
              marginBottom: "10px",
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: "10px",
              border: "1px solid #eee",
              marginTop: "15px",
            },
          },
        },
      },
    });
  }, [siteConfig]);

  if (siteConfig) {
    //const useV2 = siteConfig.id === "aqabe9A2spBl5fiN2iYb" || siteConfig.id === "-MVIf1diaYOCm5KynVDF" || siteConfig.id === "-MX2oaNrh4TcKVvnhcT9";

    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppStateContext>
          <ConfigContext.Provider value={siteConfig}>
            <Header />
            <SiteBanner />
            <Box component="main" style={{ minHeight: "100vh" }}>
              <ScrollToTop />
              <CanonicalLink />
              <Routes>
                <Route path="/get-quote/*" element={<RequestForQuote {...props} />} />
                <Route path="/verify/:phone/:reqId" element={<Verify {...props} />} />
                <Route path="/shop" element={<StandardCatalog />} />
                <Route path="/shop/:category/:slug" element={<ProductPage />} />
                <Route path="/gallery" element={<PhotoCollection />} />
                <Route path="/photo/collection/:handle" element={<PhotoCollection {...props} />} />
                <Route path="/product/:productId" element={<ProductPage />} />
                <Route path="/catering" element={<CustomPage handle="catering" />} />
                <Route path="/about" element={<CustomPage handle="about" />} />
                <Route path="/press" element={<CustomPage handle="press" />} />
                <Route path="/page/:handle" element={<CustomPage />} />
                <Route path="/events" element={<Classes />} />
                <Route path="/event/:productId" element={<ClassPage />} />
                <Route path="/classes" element={<Classes />} />
                <Route path="/class/:productId" element={<ClassPage />} />
                <Route path="/rental/:productId" element={<RentalPage />} />
                <Route path="/quote/:reqId" element={<Quote />} />
                <Route path="/requests/:reqId" element={<Quote />} />
                <Route path="/cart/:cartId/v1" element={<Cart key={Date.now()} />} />
                <Route path="/cart/:cartId" element={<CartV2 key={Date.now()} />} />
                <Route path="/booking/:cartId" element={<Booking key={Date.now()} />} />
                <Route path="/order/:orderId/balance-payment" element={<OrderBalancePay />} />
                <Route path="/order/:orderId/review" element={<OrderReview />} />
                <Route path="/order/:orderId" element={<Order />} />
                <Route path="/receipt/:orderId" element={<PaymentReceipt />} />
                <Route path="/payment-status" element={<PaymentStatusPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/flavors" element={<FlavorsPage />} />
                <Route path="/store-policy" element={<StorePolicyPage />} />
                <Route path="/accessibility" element={<AccessibilityStatement />} />
                <Route path="/unsubscribe/quote/:quoteId" element={<Unsubscribe />} />
                {/*<Route path="/privacy-policy" element={<DynamicPage {...props} handle="privacy-policy" />} />
                  <Route path="/terms-of-service" element={<DynamicPage {...props} handle="terms-of-service" />} />*/}
                <Route path="*" element={<Home {...props} />} />
              </Routes>
            </Box>
            <Footer />
          </ConfigContext.Provider>
        </AppStateContext>
      </ThemeProvider>
    );
  } else {
    return (
      <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }
}
